// src/components/learning/prof/SupportsPanel.js
// Panneau de sélection des supports pédagogiques
// Affiché quand le prof clique "📖 Supports" en session vidéo
// Organisé par niveau FirstArabic

import React, { useState } from 'react';

const C = {
  green: '#065f46',
  greenLight: '#E1F5EE',
  greenMid: '#1D9E75',
  greenBorder: '#a7f3d0',
  text: '#1a1a1a',
  muted: '#6b7280',
  white: '#fff',
  bg: '#f8f7f4',
};

// ─── DONNÉES DES NIVEAUX ─────────────────────────────
const NIVEAUX = [
  {
    id: 'abjad',
    nom: 'Abjad',
    emoji: '🌱',
    couleur: '#065f46',
    couleurLight: '#E1F5EE',
    description: 'Les 28 lettres de l\'alphabet arabe',
    disponible: true,
    lettres: [
      { lettre: 'ا', nom: 'الأَلِف', transliteration: 'Alif', ordre: 1 },
      { lettre: 'ب', nom: 'الباء', transliteration: 'Ba', ordre: 2 },
      { lettre: 'ت', nom: 'التاء', transliteration: 'Ta', ordre: 3 },
      { lettre: 'ث', nom: 'الثاء', transliteration: 'Tha', ordre: 4 },
      { lettre: 'ج', nom: 'الجيم', transliteration: 'Jim', ordre: 5 },
      { lettre: 'ح', nom: 'الحاء', transliteration: 'Ha', ordre: 6 },
      { lettre: 'خ', nom: 'الخاء', transliteration: 'Kha', ordre: 7 },
      { lettre: 'د', nom: 'الدال', transliteration: 'Dal', ordre: 8 },
      { lettre: 'ذ', nom: 'الذال', transliteration: 'Dhal', ordre: 9 },
      { lettre: 'ر', nom: 'الراء', transliteration: 'Ra', ordre: 10 },
      { lettre: 'ز', nom: 'الزاي', transliteration: 'Zayn', ordre: 11 },
      { lettre: 'س', nom: 'السين', transliteration: 'Sin', ordre: 12 },
      { lettre: 'ش', nom: 'الشين', transliteration: 'Shin', ordre: 13 },
      { lettre: 'ص', nom: 'الصاد', transliteration: 'Sad', ordre: 14 },
      { lettre: 'ض', nom: 'الضاد', transliteration: 'Dad', ordre: 15 },
      { lettre: 'ط', nom: 'الطاء', transliteration: 'Ta emp.', ordre: 16 },
      { lettre: 'ظ', nom: 'الظاء', transliteration: 'Dha emp.', ordre: 17 },
      { lettre: 'ع', nom: 'العين', transliteration: 'Ayn', ordre: 18 },
      { lettre: 'غ', nom: 'الغين', transliteration: 'Ghayn', ordre: 19 },
      { lettre: 'ف', nom: 'الفاء', transliteration: 'Fa', ordre: 20 },
      { lettre: 'ق', nom: 'القاف', transliteration: 'Qaf', ordre: 21 },
      { lettre: 'ك', nom: 'الكاف', transliteration: 'Kaf', ordre: 22 },
      { lettre: 'ل', nom: 'اللام', transliteration: 'Lam', ordre: 23 },
      { lettre: 'م', nom: 'الميم', transliteration: 'Mim', ordre: 24 },
      { lettre: 'ن', nom: 'النون', transliteration: 'Nun', ordre: 25 },
      { lettre: 'ه', nom: 'الهاء', transliteration: 'Ha', ordre: 26 },
      { lettre: 'و', nom: 'الواو', transliteration: 'Waw', ordre: 27 },
      { lettre: 'ي', nom: 'الياء', transliteration: 'Ya', ordre: 28 },
    ]
  },
  {
    id: 'nur',
    nom: 'Nur',
    emoji: '⭐',
    couleur: '#92400e',
    couleurLight: '#fffbeb',
    description: 'Vocabulaire et phrases simples',
    disponible: false,
    lettres: []
  },
  {
    id: 'qalam',
    nom: 'Qalam',
    emoji: '🌙',
    couleur: '#1e40af',
    couleurLight: '#eff6ff',
    description: 'Lecture et écriture avancée',
    disponible: false,
    lettres: []
  },
];

const SupportsPanel = ({ onClose, onSelectLettre }) => {
  const [niveauActif, setNiveauActif] = useState('abjad');
  const [search, setSearch] = useState('');

  const niveau = NIVEAUX.find(n => n.id === niveauActif);
  const lettresFiltrees = niveau?.lettres.filter(l =>
    l.lettre.includes(search) ||
    l.nom.includes(search) ||
    l.transliteration.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.75)',
      zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: C.white, borderRadius: 20,
        width: '100%', maxWidth: 720,
        maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `0.5px solid #e5e7eb`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${C.green}, #047857)`,
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700 }}>
              📖 Supports Pédagogiques
            </h2>
            <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              Sélectionnez une lettre pour commencer le cours
            </p>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.1)',
            color: '#fff', fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Onglets niveaux */}
        <div style={{
          display: 'flex', gap: 0,
          borderBottom: `0.5px solid #e5e7eb`,
          background: C.bg,
          padding: '12px 24px 0',
        }}>
          {NIVEAUX.map(n => (
            <button key={n.id} onClick={() => n.disponible && setNiveauActif(n.id)} style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: niveauActif === n.id ? `2px solid ${n.couleur}` : '2px solid transparent',
              background: 'none',
              color: !n.disponible ? '#ccc' : niveauActif === n.id ? n.couleur : C.muted,
              fontWeight: niveauActif === n.id ? 700 : 400,
              fontSize: 14, cursor: n.disponible ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.2s',
            }}>
              {n.emoji} {n.nom}
              {!n.disponible && (
                <span style={{
                  fontSize: 9, background: '#f0f0f0', color: '#999',
                  padding: '2px 6px', borderRadius: 10, fontWeight: 600,
                }}>
                  bientôt
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Barre de recherche */}
        <div style={{ padding: '16px 24px 8px' }}>
          <input
            type="text"
            placeholder="🔍 Rechercher une lettre..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px',
              border: '0.5px solid #e5e7eb', borderRadius: 10,
              fontSize: 14, outline: 'none', boxSizing: 'border-box',
              background: C.bg,
            }}
          />
        </div>

        {/* Description niveau */}
        {niveau && (
          <div style={{
            padding: '0 24px 12px',
            fontSize: 13, color: C.muted,
          }}>
            {niveau.emoji} <strong style={{ color: niveau.couleur }}>{niveau.nom}</strong> — {niveau.description} · {lettresFiltrees.length} supports disponibles
          </div>
        )}

        {/* Grille des lettres */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: '8px 24px 24px',
        }}>
          {niveau?.disponible ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 12,
            }}>
              {lettresFiltrees.map((l) => (
                <div
                  key={l.lettre}
                  onClick={() => onSelectLettre(l.lettre)}
                  style={{
                    background: C.white,
                    border: `0.5px solid #e5e7eb`,
                    borderRadius: 14,
                    padding: '16px 8px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = niveau.couleurLight;
                    e.currentTarget.style.borderColor = niveau.couleur;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = C.white;
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Numéro ordre */}
                  <div style={{
                    position: 'absolute', top: 6, right: 8,
                    fontSize: 9, color: '#ccc', fontWeight: 600,
                  }}>
                    {l.ordre}
                  </div>

                  {/* Lettre */}
                  <div style={{
                    fontSize: 48, lineHeight: 1,
                    color: niveau.couleur,
                    fontFamily: "'Amiri', 'Traditional Arabic', serif",
                    direction: 'rtl',
                    marginBottom: 8,
                  }}>
                    {l.lettre}
                  </div>

                  {/* Nom arabe */}
                  <div style={{
                    fontSize: 11, color: C.text,
                    direction: 'rtl', marginBottom: 2,
                    fontFamily: "'Amiri', serif",
                  }}>
                    {l.nom}
                  </div>

                  {/* Translitération */}
                  <div style={{ fontSize: 10, color: C.muted }}>
                    {l.transliteration}
                  </div>

                  {/* Badge "Complet" pour ي (données complètes) */}
                  {l.lettre === 'ي' && (
                    <div style={{
                      position: 'absolute', top: 6, left: 6,
                      width: 8, height: 8, borderRadius: '50%',
                      background: '#10b981',
                    }} title="Support complet" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center', padding: '60px 20px',
              color: C.muted,
            }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>{niveau?.emoji}</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                Niveau {niveau?.nom} — Bientôt disponible
              </div>
              <div style={{ fontSize: 13 }}>
                Ce niveau sera disponible après la complétion du niveau Abjad
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 24px',
          borderTop: '0.5px solid #e5e7eb',
          background: C.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 12, color: C.muted,
        }}>
          <span>🔒 Supports réservés à la session vidéo</span>
          <span style={{
            background: C.greenLight, color: C.green,
            padding: '3px 10px', borderRadius: 20, fontWeight: 600,
          }}>
            Mode Professeur
          </span>
        </div>
      </div>
    </div>
  );
};

export default SupportsPanel;
